import { forwardRef } from 'react';
import { SelectBox } from './SelectBox';
import type { SelectBoxProps } from './SelectBox';

/**
 * Props {@link ComboBox} — sama dengan {@link SelectBoxProps}.
 */
export type ComboBoxProps<T = unknown> = SelectBoxProps<T>;

/**
 * DERIVATIF: SelectBox dengan **searchable** built-in.
 *
 * Secara teknis underlying-nya sama dengan {@link SelectBox} — bedanya
 * hanya `showSearch` defaultnya `true` + `optionFilterProp="label"` supaya
 * search match terhadap label option.
 *
 * Pakai komponen ini untuk:
 * - Select dengan **banyak option** (>10) → user butuh ketik untuk filter.
 * - Picker dengan label panjang (mis. nama akun, kode COA, dll).
 *
 * Pakai {@link SelectBox} biasa untuk:
 * - Select dengan **sedikit option** (<10) → tidak perlu search.
 * - Status, kategori sederhana.
 *
 * Pemakaian:
 * ```tsx
 * <ComboBox
 *   value={kodeCoa}
 *   onChange={setKodeCoa}
 *   options={coaList.map((c) => ({ value: c.coa, label: `${c.coa} - ${c.nama}` }))}
 *   placeholder="Cari COA..."
 * />
 * ```
 *
 * Dokumentasi pattern: `.claude/docs/Booku v2/ui-patterns/control-system-pattern.md`
 */
function ComboBoxInner<T = unknown>(
  props: ComboBoxProps<T>,
  ref: React.Ref<unknown>
) {
  return (
    <SelectBox<T>
      ref={ref}
      showSearch
      optionFilterProp="label"
      {...props}
    />
  );
}

export const ComboBox = forwardRef(ComboBoxInner) as <T = unknown>(
  props: ComboBoxProps<T> & { ref?: React.Ref<unknown> }
) => React.ReactElement;
